import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { combineLatest, fromEvent as observableFromEvent, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { PartnerService } from '../core/partner.service';
import { UserService } from '../users';
import { OmniSearchService } from './omni-search.service';

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  type: string;
  unifiedId: string;
}

@Component({
  templateUrl: './user-switcher.component.html',
  styleUrls: ['./common.scss', './user-switcher.component.scss'],
  standalone: false,
})
export class UserSwitcherComponent implements AfterViewInit {
  urlForSalespeopleLookup = '/_ajax/v1/salesperson/list';

  @ViewChild('searchTerm', { static: true }) searchTerm: ElementRef;
  people$: Observable<Person[]>;

  constructor(
    public dialogRef: MatDialogRef<UserSwitcherComponent>,
    private userService: UserService,
    private ssoService: SSOService,
    private snackbarService: SnackbarService,
    private partnerService: PartnerService,
    private config: AppConfigService,
    private omnisearchService: OmniSearchService,
    private http: HttpClient,
  ) {}

  ngAfterViewInit(): void {
    const keyUps$ = observableFromEvent(this.searchTerm.nativeElement, 'keyup').pipe(
      distinctUntilChanged(),
      map((_) => this.searchTerm.nativeElement.value),
      debounceTime(300),
    );
    this.people$ = keyUps$.pipe(
      switchMap((searchTerm) => {
        return this.userService.search(of(searchTerm), 0);
      }),
      filter((users) => !!users),
      map((users) =>
        users.map((u) => {
          return {
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            phoneNumber: u.phoneNumber,
            id: u.userId,
            unifiedId: u.unifiedUserId,
            type: 'U',
          };
        }),
      ),
    );
    if (this.config.config.userCanAccessSales) {
      // Get salespeople into the mix as well
      const salesPeople$ = keyUps$.pipe(
        switchMap((searchTerm) => {
          return this.http.get<{ data: any[] }>(this.urlForSalespeopleLookup, { params: { searchText: searchTerm } });
        }),
        map((resp: any) => resp.data),
        map((salespeople) => {
          return salespeople.map((sp) => {
            return {
              firstName: sp.first_name,
              lastName: sp.last_name,
              id: sp.sales_person_id,
              email: sp.email,
              phoneNumber: sp.phone_numbers?.[0],
              unifiedId: sp.unifiedUserId,
              type: 'S',
            };
          });
        }),
      );

      this.people$ = combineLatest([this.people$, salesPeople$]).pipe(
        map(([users, salespeople]) => {
          return [...users, ...salespeople].sort((a, b) => {
            if (a.firstName > b.firstName) {
              return 1;
            }
            if (a.firstName < b.firstName) {
              return -1;
            }
            return 0;
          });
        }),
      );
    }
  }

  impersonateUser(user: Person): void {
    const serviceProviderId = user.type === 'U' ? 'VBC' : 'ST';
    this.ssoService
      .getEntryUrl(serviceProviderId, newPartnerServiceContext(this.partnerService.partnerId), {
        impersonation: user.unifiedId,
      })
      .subscribe({
        next: (url) => {
          if (url === undefined || url === 'undefined') {
            this.snackbarService.errorSnack('Error Impersonating User');
            return;
          }
          window.location.href = url;
          this.dialogRef.close();
        },
        error: () => this.snackbarService.errorSnack('Error Impersonating User'),
      });
  }
}
